import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, type Me } from '@/lib/api'
import { usePageFrame } from '@/lib/frame'
import { applyPalette } from '@/lib/palette'
import { applyTheme } from '@/lib/theme'
import {
  cleanPalette,
  colorsFor,
  DEFAULTS,
  EMPTY_PALETTE,
  HEX,
  isEmpty,
  lowContrast,
  PALETTE_GROUPS,
  type Mode,
  type Palette,
  type PaletteRole,
} from '../../shared/palette'

const MODE_CHOICES: [Mode, string][] = [
  ['light', 'Light'],
  ['dark', 'Dark'],
]

// Admin → Colours: the owner picks every colour on the site, in light and dark. Each change shows on the page at
// once; nothing reaches anyone else until Save. Leaving without saving puts the saved colours back.
export function PaletteEditor({ me }: { me: Me }) {
  usePageFrame(undefined, { title: 'Colours', back: { to: '/me', label: 'Profile' } })
  const queryClient = useQueryClient()
  const saved = cleanPalette(me.palette)
  const [draft, setDraft] = useState<Palette>(saved)
  const [mode, setMode] = useState<Mode>(document.documentElement.classList.contains('dark') ? 'dark' : 'light')
  const save = useMutation({
    mutationFn: (palette: Palette) => api<{ palette: Palette }>('/admin/palette', { method: 'PUT', body: palette }),
    onSuccess: ({ palette }) => {
      setDraft(palette)
      return queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })

  // What's being edited is what's shown: the draft's colours, in the mode being edited.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark')
    applyPalette(draft)
  }, [draft, mode])
  // And on the way out, the account's own light or dark and the saved colours (read fresh, in case they just saved).
  useEffect(
    () => () => {
      applyTheme(me.preferences.theme)
      applyPalette(queryClient.getQueryData<Me>(['me'])?.palette ?? me.palette)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const colors = colorsFor(draft, mode)
  const set = (role: PaletteRole, value: string) =>
    setDraft((d) => cleanPalette({ ...d, [mode]: { ...d[mode], [role]: value } }))
  const changed = JSON.stringify(draft) !== JSON.stringify(saved)
  const hard = lowContrast(draft, mode)

  return (
    <>
      <header className="flex flex-col gap-1.5">
        <p className="eyebrow">Admin</p>
        <h1 className="text-display">Colours</h1>
        <p className="text-sub text-muted-foreground">
          Every colour on gatheredfrom, for everyone. Changes show here as you make them and reach everyone when you
          save.
        </p>
      </header>
      <Choice
        legend="Editing"
        hint={`You’re seeing ${mode} mode while you edit it. Everyone else sees the mode they chose.`}
        value={mode}
        options={MODE_CHOICES}
        onChange={(v) => setMode(v as Mode)}
      />
      {PALETTE_GROUPS.map((group) => (
        <fieldset key={group.title} className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
          <legend className="mb-1 text-title">{group.title}</legend>
          {'hint' in group && <p className="text-sub text-muted-foreground">{group.hint}</p>}
          <ul className="flex flex-col divide-y divide-border/70">
            {group.roles.map(([role, label]) => (
              <ColorRow
                key={role}
                label={label}
                value={colors[role]}
                changed={colors[role] !== DEFAULTS[mode][role]}
                onChange={(v) => set(role, v)}
                onReset={() => set(role, DEFAULTS[mode][role])}
              />
            ))}
          </ul>
        </fieldset>
      ))}
      <FormError error={save.error} />
      {/* Kept in view while scrolling the colours: the save, and what a change has made hard to read. */}
      <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {hard.length > 0 && (
          <Alert>
            <TriangleAlert aria-hidden />
            <AlertDescription>
              <p className="font-medium text-foreground">Hard to read in {mode} mode</p>
              <ul>
                {hard.map((x) => (
                  <li key={x.label}>
                    {x.label}: {x.ratio.toFixed(1)} to 1, where {x.min} is easy to read
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={!changed || save.isPending} onClick={() => save.mutate(draft)}>
            Save colours
          </Button>
          <Button type="button" variant="secondary" disabled={!changed} onClick={() => setDraft(saved)}>
            Undo changes
          </Button>
          <Button type="button" variant="ghost" disabled={isEmpty(draft)} onClick={() => setDraft(EMPTY_PALETTE)}>
            Original colours
          </Button>
        </div>
      </div>
      <p className="text-sub text-muted-foreground">
        Back to your{' '}
        <Link to="/me" className="text-brand underline underline-offset-4">
          profile
        </Link>
        .
      </p>
    </>
  )
}

function ColorRow({
  label,
  value,
  changed,
  onChange,
  onReset,
}: {
  label: string
  value: string
  changed: boolean
  onChange: (value: string) => void
  onReset: () => void
}) {
  // What's being typed, while it's typed: it counts once it's a whole six-digit colour.
  const [typed, setTyped] = useState<string | null>(null)
  return (
    <li className="flex items-center gap-3 py-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="size-11 shrink-0 cursor-pointer rounded-full border border-input bg-transparent p-0.5 [&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
      />
      <span className="min-w-0 flex-1 text-sm">{label}</span>
      <Input
        value={typed ?? value}
        onChange={(e) => {
          const next = e.target.value.trim().toLowerCase()
          setTyped(next)
          if (HEX.test(next)) onChange(next)
        }}
        onBlur={() => setTyped(null)}
        aria-label={`${label} colour code`}
        spellCheck={false}
        maxLength={7}
        className="w-26 shrink-0 px-3 font-mono text-sm"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={changed ? '' : 'invisible'}
        onClick={onReset}
        aria-label={`Put back ${label}`}
        title="Put back the original"
      >
        <RotateCcw aria-hidden />
      </Button>
    </li>
  )
}
